import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { ComponentProps, useEffect } from 'react';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import { CLAIM_WARMUP_OBJECTIVE_MUTATION } from 'components/leaderboard/WarmupRewards/ClaimObjectiveInlineButton';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';

import { Title } from '../LastFixtureStoryStep/Title';
import { StoryState } from '../types';
import type {
  CompletedOnboardingTask_so5Fixture,
  CompletedOnboardingTask_taskInterface,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  aspect-ratio: var(--card-aspect-ratio);
  border: 1px solid var(--c-brand-600);
  border-radius: var(--unit);
  background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-brand-600), 0.2) 0%,
      rgba(var(--c-rgb-brand-600), 0) 49.95%
    ),
    var(--c-black);
  overflow: hidden;
  position: relative;
`;

const InnerWrapper = styled(Vertical)`
  flex: 1;
  padding: var(--triple-unit);
  align-items: flex-start;
`;

const CenterContent = styled(Vertical).attrs({ center: true })`
  flex: 1;
  align-self: center;
`;

const HeaderImg = styled.img`
  width: 120%;
  object-fit: contain;
  left: -10%;
  position: absolute;
  bottom: 0;
`;

type Props = {
  from?: StoryState;
  so5Fixture?: CompletedOnboardingTask_so5Fixture;
  completedOnboardingTasks: CompletedOnboardingTask_taskInterface[];
  onNext?: ComponentProps<typeof LastFixtureStoryStep>['onNext'];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};

// we don't have leaderboard linked to the completed tasks from API so let's use hardcoded values for now
const HARDCODED_BOE_LEAGUE_HEADER =
  'https://assets.sorare.com/so5league/78c36725-cc47-422b-885e-70aaa624eb8b/picture/header-f7f0907ede969b5facbe4e0295197cb7.png';
const HARDCODED_BOE_DIVISION_LOGO =
  'https://frontend-assets.sorare.com/football/manager_team/common/icon.png';
type ContentProps = {
  completedOnboardingTasks: Props['completedOnboardingTasks'];
};
const Content = ({ completedOnboardingTasks }: ContentProps) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const itemToDisplayOnDesktop = Math.min(3, completedOnboardingTasks.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;

  return (
    <div
      style={{
        width: `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`,
      }}
    >
      <Scrollable itemToDisplay={itemToDisplay} withMask>
        {completedOnboardingTasks.map(completedOnboardingTask => (
          <Wrapper key={completedOnboardingTask.id}>
            <InnerWrapper>
              <FontAwesomeIcon
                icon={faCheckCircle}
                color="var(--c-score-high)"
                size="xl"
              />
              <CenterContent gap={6}>
                <img
                  src={HARDCODED_BOE_DIVISION_LOGO}
                  alt=""
                  width="80"
                  height="80"
                />
                <LabelL bold className="text-center">
                  {completedOnboardingTask.description}
                </LabelL>
              </CenterContent>
            </InnerWrapper>
            <HeaderImg src={HARDCODED_BOE_LEAGUE_HEADER} alt="" />
          </Wrapper>
        ))}
      </Scrollable>
    </div>
  );
};

export const CompletedOnboardingTask = ({
  from,
  so5Fixture,
  completedOnboardingTasks,
  onNext,
  onClose,
}: Props) => {
  const { intro, tunnel } = useResultsStoryAssets();
  const [claim] = useMutation(CLAIM_WARMUP_OBJECTIVE_MUTATION);

  useEffect(() => {
    completedOnboardingTasks.forEach(async task => {
      if (task.aasmState !== ManagerProgressionTaskState.COMPLETED) {
        return;
      }
      await claim({
        variables: { name: task.name },
      });
    });
  }, [completedOnboardingTasks, claim]);

  return (
    <LastFixtureStoryStep
      title={<Title type="completedOnboardingTask" so5Fixture={so5Fixture} />}
      video={{
        asset: from === 'intro' ? intro : tunnel,
        color: '#00FF18',
        colorDelay: from === 'intro' ? 800 : 0,
      }}
      onNext={onNext}
      onClose={onClose}
    >
      <Content completedOnboardingTasks={completedOnboardingTasks} />
    </LastFixtureStoryStep>
  );
};

CompletedOnboardingTask.fragments = {
  so5Fixture: gql`
    fragment CompletedOnboardingTask_so5Fixture on So5Fixture {
      slug
      ...Title_so5Fixture
    }
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<CompletedOnboardingTask_so5Fixture>,
  taskInterface: gql`
    fragment CompletedOnboardingTask_taskInterface on TaskInterface {
      id
      name
      description
      aasmState
    }
  ` as TypedDocumentNode<CompletedOnboardingTask_taskInterface>,
};
